import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import {Router} from '@angular/router';
import {FileSharingDialogComponent} from '../dialogs/file-sharing-dialog/file-sharing-dialog.component';
import {SharedFile} from '../documents/shared-file';
import {SharedFileSection} from '../documents/shared-file-section';
import {SharedFileType} from '../documents/shared-file-type';
import {PersonLogin} from '../person/person-login';
import {DocumentService} from '../services/document-service';
import {PersonLoginService} from '../services/person-login-service';
import {StringUtils} from '../utils/string-utils';
import {MatDialog} from '@angular/material/dialog';
import {EncompassTreeNode} from '../model/encompass-tree-node';
import {FileUtils} from "../utils/file-utils";
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DocumentationTreeComponent } from './documentation-tree/documentation-tree.component';


@Component({
    selector: 'marketing',
    templateUrl: './marketing.component.html',
    styleUrls: ['./marketing.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DocumentationTreeComponent, MatProgressSpinner]
})
export class MarketingComponent implements OnInit {
  readonly documents = input<string>(undefined!);
  readonly software = input<string>(undefined!);
  readonly revit = input<string>(undefined!);
  readonly presentations = input<string>(undefined!);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly documentService = inject(DocumentService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);


  private personLogin!: PersonLogin;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly openDocumentation = signal<boolean>(undefined!);
  readonly openSoftware = signal<boolean>(undefined!);
  readonly openRevit = signal<boolean>(undefined!);
  readonly openPresentations = signal<boolean>(undefined!);
  readonly documentationNodes = signal<EncompassTreeNode[]>([]);
  readonly softwareNodes = signal<EncompassTreeNode[]>([]);
  readonly revitFileNodes = signal<EncompassTreeNode[]>([]);
  readonly presentationFileNodes = signal<EncompassTreeNode[]>([]);

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      if (!this.personLogin) {
        this.router.navigate(['main']);
      } else {
        this.clearStatus();
        this.getMarketingDocuments();
      }
      this.openDocumentation.set(!!this.documents());
      this.openSoftware.set(!!this.software());
      this.openRevit.set(!!this.revit());
      this.openPresentations.set(!!this.presentations());
    }
  }

  public getMarketingDocuments() {
    this.isBusy.set(true);
    this.documentService.getMarketingDocuments().subscribe(result => {
      this.documentationNodes.set(this.documentService.sortNodes(result.nodes));
      this.documentService.getMarketingSoftware().subscribe(software => {
        this.softwareNodes.set(this.documentService.sortNodes(software.nodes));
        this.documentService.getRevitFiles().subscribe(revit => {
          this.revitFileNodes.set(this.documentService.sortNodes(revit.nodes));
          this.documentService.getPresentationFiles().subscribe(presentation => {
            const placeholderFile = presentation.nodes[0].children.filter(node => node.displayName.toUpperCase() === 'THUMBS.DB.NONE');
            if (placeholderFile.length === 1) {
              presentation.nodes[0].children.splice(presentation.nodes[0].children.indexOf(placeholderFile[0]), 1);
            }
            this.presentationFileNodes.set(this.documentService.sortNodes(presentation.nodes));
            this.isBusy.set(false);
          }, error => this.handleError(error));
        }, error => this.handleError(error));
      }, error => this.handleError(error));
    }, error => this.handleError(error));
  }

  public download(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentService.downloadMarketingDocument(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  public downloadSoftware(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentService.downloadMarketingSoftware(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  public downloadRevit(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentService.downloadRevitFile(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  public downloadPresentation(node: EncompassTreeNode) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentService.downloadPresentationFile(node.name).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, node.displayName);
    }, error => {
      this.handleError(error);
    });
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentationNodes.set([]);
    this.softwareNodes.set([]);
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  shareSoftware(fileName: string): void {
    let sharedFile: SharedFile = this.buildSharedFile(SharedFileType.SOFTWARE, fileName);
    this.openFileShareDialog(sharedFile);
  }

  private openFileShareDialog(sharedFile: SharedFile): void {
    this.dialog.open(FileSharingDialogComponent, {
      width: '680px',
      height: '200px',
      data: {sharedFile: sharedFile},
    });
  }

  private buildSharedFile(type: SharedFileType, fileName: string): SharedFile {
    let sharedFile: SharedFile = new SharedFile();
    sharedFile.fileName = fileName;
    sharedFile.section = SharedFileSection.MARKETING;
    sharedFile.type = type;
    sharedFile.validDays = 3;
    return sharedFile;
  }

  shareRevit(fileName: string): void {
    let sharedFile: SharedFile = this.buildSharedFile(SharedFileType.REVIT, fileName);
    this.openFileShareDialog(sharedFile);
  }

}
